import { createSelector } from '@app/utils/createSelector'
import { errorToFetchDataError } from '@app/utils/performFetchData'

import { isApiError } from '@app/packages/api/errors'
import { asError } from '@app/packages/asError/asError'
import { isLoaded } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { profileUserSelector } from './profile'

const emailConfirmationSlice = createReduxSlice<{ type: 'resent'; email: string }>('email_confirmation')
/** The address the notice was closed for. Only a closable notice reads it: the rest stay until it is confirmed. */
const emailConfirmationClosedSlice = createReduxSlice<{ email: string }>('email_confirmation_closed')
/**
 * A link is spent once it is used, and the backend answers a spent one exactly as it answers a
 * mistyped one: 400 WRONG_CONFIRMATION_TOKEN. So the page says what is true of both — the link
 * does not work, the address may already be confirmed — rather than showing an error screen for
 * someone who did nothing wrong.
 */
export type EmailVerification = { status: 'confirmed'; email: string } | { status: 'invalid_token' }

const emailVerificationSlice = createReduxSlice<EmailVerification>('email_verification')

export const emailConfirmationStateSelector = createSelector(
  [emailConfirmationSlice.selector, emailConfirmationClosedSlice.selector, profileUserResultSelector],
  (confirmation, closedFor, userResult) => {
    const user = !userResult.error && isLoaded(userResult.value.value) ? userResult.value.value : null
    if (!user) return null
    if (!('email_confirmed' in user)) return null
    if (user.email_confirmed) return null
    if (!user.email) return null
    const closed = closedFor?.email === user.email
    if (!confirmation || confirmation.email !== user.email) return { type: 'unverified' as const, email: user.email, closed }
    return { ...confirmation, closed }
  }
)

export const emailVerificationSelector = emailVerificationSlice.selector

export const confirmEmail = (token: string) =>
  createThunk(async dispatch => {
    const [{ postEmailConfirmation }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/email_confirmation'),
      import('@app/store/actions/api'),
    ])
    try {
      const resp = await dispatch(callV2Api(postEmailConfirmation(token)))
      dispatch(emailVerificationSlice.set({ status: 'confirmed', email: resp.data.attributes.email }))
    } catch (e) {
      if (isWrongToken(e)) return dispatch(emailVerificationSlice.set({ status: 'invalid_token' }))

      throw dispatch(errorToFetchDataError(asError(e), 'Email confirmation fetch failed', { expose: true }))
    }
  })

export const hideEmailConfirmationMessage = () =>
  createThunk((dispatch, getState) => {
    const email = profileUserSelector(getState())?.email
    if (!email) throw new Error('No email to hide confirmation')
    dispatch(emailConfirmationClosedSlice.set({ email }))
  })

export const resendEmailConfirmation = () =>
  createThunk(async (dispatch, getState) => {
    const [{ postEmailConfirmationResend }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/email_confirmation'),
      import('@app/store/actions/api'),
    ])
    const email = profileUserSelector(getState())?.email
    if (!email) throw new Error('No email to resend confirmation')
    await dispatch(callV2Api(postEmailConfirmationResend()))
    dispatch(emailConfirmationSlice.set({ type: 'resent', email }))
  })

const isWrongToken = (e: unknown) => isApiError(e) && e.response?.errors?.length === 1 && e.response.errors[0].code === 'WRONG_CONFIRMATION_TOKEN'
